import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import 'bootstrap/dist/css/bootstrap.css';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import keycloak from './keycloak';
import Keycloak from 'keycloak-js';

const SKIP_AUTH = process.env.REACT_APP_SKIP_AUTH === 'true';

// Vorbefüllte Textfelder: beim Fokussieren (Antippen) den Inhalt komplett
// markieren — Tippen ersetzt ihn dann sofort, ohne manuelles Löschen.
// Wichtig v.a. am Handy bei übernommenen/vorbefüllten Altwerten; wer nur
// hineinschaut und nichts tippt, verliert nichts. (setTimeout: iOS Safari
// verwirft select() direkt im focus-Event.)
document.addEventListener('focusin', (ev) => {
  const el = ev.target as HTMLInputElement;
  if (!el || el.readOnly) return;
  const tag = el.tagName;
  const typ = (el.type || '').toLowerCase();
  if ((tag === 'INPUT' && ['text', 'email', 'tel'].includes(typ)) || tag === 'TEXTAREA') {
    if (el.value) setTimeout(() => { try { el.select(); } catch { /* egal */ } }, 0);
  }
});

const mockKeycloak = new Keycloak({ url: 'http://localhost', realm: 'mock', clientId: 'mock' });
(mockKeycloak as any).authenticated = true;
(mockKeycloak as any).tokenParsed = { name: 'Dev User', preferred_username: 'dev' };
(mockKeycloak as any).token = 'mock-token';
(mockKeycloak as any).logout = () => { window.location.reload(); };
mockKeycloak.init = () => Promise.resolve(true);
mockKeycloak.updateToken = () => Promise.resolve(true);

const authClient = SKIP_AUTH ? mockKeycloak : keycloak;

const AuthErrorScreen: React.FC<{ error: any }> = ({ error }) => (
  <div className="kc-loading">
    <div className="kc-loading__glow" />
    <div className="kc-loading__content">
      <div className="kc-loading__logo-ring">
        <svg className="kc-loading__ring-svg" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(192,57,43,0.2)" strokeWidth="2" />
          <circle cx="60" cy="60" r="54" fill="none" stroke="#c0392b" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="339" strokeDashoffset="0" />
        </svg>
        <div className="kc-loading__logo-dot" style={{ background: '#c0392b' }} />
      </div>
      <p className="kc-loading__text" style={{ color: '#c0392b' }}>Anmeldung fehlgeschlagen</p>
      <p style={{ color: '#888', fontSize: '0.85rem', maxWidth: 340, textAlign: 'center', lineHeight: 1.5 }}>
        {error?.error === 'unauthorized_client'
          ? 'Der Auth-Server hat den Client abgelehnt. Bitte prüfe die Keycloak-Konfiguration (Redirect-URIs).'
          : `Verbindung zum Auth-Server fehlgeschlagen: ${error?.error || 'Unbekannter Fehler'}`}
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          marginTop: 12, padding: '8px 24px', border: '1px solid #c0392b', borderRadius: 6,
          background: 'transparent', color: '#c0392b', cursor: 'pointer', fontSize: '0.9rem',
        }}
      >
        Erneut versuchen
      </button>
    </div>
  </div>
);

const KeycloakWrapper: React.FC = () => {
  const [authError, setAuthError] = useState<any>(null);

  const onKeycloakEvent = useCallback((event: string, error?: any) => {
    if (event === 'onInitError' || event === 'onAuthError') {
      setAuthError(error || { error: event });
    }
  }, []);

  const onKeycloakTokens = useCallback((_tokens: any) => {}, []);

  if (authError) {
    return <AuthErrorScreen error={authError} />;
  }

  return (
    <ReactKeycloakProvider
      authClient={authClient}
      onEvent={onKeycloakEvent}
      onTokens={onKeycloakTokens}
      initOptions={SKIP_AUTH ? { onLoad: undefined as any } : {
        onLoad: 'login-required',
        checkLoginIframe: false,
        silentCheckSsoFallback: false,
        // birthdate kommt aus dem FO24->Keycloak-Sync; der Gateway nutzt es
        // (plus preferred_username/Name) fuer das Live-Matching Token->USP-p_id.
        scope: 'openid profile birthdate email',
      }}
      LoadingComponent={SKIP_AUTH ? undefined : (
        <div className="kc-loading">
          <div className="kc-loading__glow" />
          <div className="kc-loading__content">
            <div className="kc-loading__logo-ring">
              <svg className="kc-loading__ring-svg" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(192,57,43,0.1)" strokeWidth="2" />
                <circle cx="60" cy="60" r="54" fill="none" stroke="#c0392b" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="50 290" className="kc-loading__ring-spin" />
              </svg>
              <div className="kc-loading__logo-dot" />
            </div>
            <p className="kc-loading__text">Anmeldung</p>
            <div className="kc-loading__runway">
              <div className="kc-loading__runway-light" />
              <div className="kc-loading__runway-light" />
              <div className="kc-loading__runway-light" />
              <div className="kc-loading__runway-light" />
              <div className="kc-loading__runway-light" />
            </div>
          </div>
        </div>
      )}
    >
      <App />
    </ReactKeycloakProvider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(<KeycloakWrapper />);

reportWebVitals();
